/**
 * useRegionWorkshops — every workshop in a region for the given window,
 * `mine`-flagged for the signed-in teacher.
 *
 * `GET /api/v1/workshops` pages by offset; a full page (`limit` rows back)
 * means there may be more, so this keeps asking until a page comes back
 * short. `limit` is the endpoint's max, so a region rarely needs more than
 * one round trip.
 */
import { crewRoleOf } from '@bonsai/shared';
import { useQuery } from '@tanstack/react-query';

import { fetchWorkshops, type Workshop } from '../../../api/workshops';
import { accentFor, fallbackVenueShort } from '../lib/workshop-chips';
import type { WorkshopChip } from '../primitives';

const STALE_30S = 30_000;
const PAGE_LIMIT = 500;

async function fetchAllPages(
  regionId: string,
  fromDate: string,
  toDate: string,
): Promise<Workshop[]> {
  const pages: Workshop[] = [];
  let offset = 0;
  // Pages are dependent — each one decides whether the next is fetched at
  // all — so they can't be issued concurrently.
  for (;;) {
    const page = await fetchWorkshops({
      regionId,
      from: fromDate,
      to: toDate,
      limit: PAGE_LIMIT,
      offset,
    });
    pages.push(...page);
    if (page.length < PAGE_LIMIT) break;
    offset += PAGE_LIMIT;
  }
  return pages;
}

/** Every workshop in a region between two dates (`YYYY-MM-DD`, both ends
 *  included), as calendar chips. */
export async function fetchRegionChips(
  regionId: string,
  fromDate: string,
  toDate: string,
): Promise<WorkshopChip[]> {
  const workshops = await fetchAllPages(regionId, fromDate, toDate);
  return workshops.map(toRegionWorkshopChip);
}

/** A region's workshops starting on the given dates (`YYYY-MM-DD`, both ends
 *  included), `mine` when the signed-in teacher is on it. Waits for a region
 *  and both ends of the range. */
export function useRegionWorkshops(regionId?: string, fromDate?: string, toDate?: string) {
  return useQuery({
    queryKey: ['me', 'region-workshops', regionId, fromDate, toDate],
    queryFn: () => fetchRegionChips(regionId as string, fromDate as string, toDate as string),
    staleTime: STALE_30S,
    enabled: Boolean(regionId && fromDate && toDate),
  });
}

/** Map a `GET /api/v1/workshops` row to the shared calendar chip shape. */
function toRegionWorkshopChip(w: Workshop): WorkshopChip {
  const crew = w.crew;
  return {
    id: w.id,
    date: w.startsAt,
    localDate: w.localStartDatetime,
    venueName: w.venueName,
    venueShort: w.venueShort ?? fallbackVenueShort(w.venueName),
    species: w.workshopType.treeSpecies?.name ?? null,
    capacity: w.capacity,
    booked: w.attendeesBooked,
    leadInstructor: crew?.teacher ?? null,
    assistant: crew?.assistant ?? null,
    accentColor: accentFor(w),
    subregionLabel: w.subregionLabel ?? undefined,
    regionSlug: w.regionSlug ?? null,
    lifecycle: w.lifecycle,
    replacements: w.treeReplacementsBooked,
    // This endpoint's `myRole` is the raw crew position, not a role.
    mine: w.myRole !== null && w.myRole !== undefined,
    myRole: w.myRole ? crewRoleOf(w.myRole) : null,
  };
}
